// src/admin/pages/Newsletter/newsletterService.ts
import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

export interface NewsletterSubscriber {
  id: string;
  email: string;
  isActive: boolean;
  subscribedAt?: Date;
}

export interface PaginatedNewsletterResult {
  subscribers: NewsletterSubscriber[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface NewsletterStats {
  totalSubscribers: number;
  activeSubscribers: number;
  inactiveSubscribers: number;
}

const COLLECTION = 'newsletters';

export class NewsletterService {

  /* ===================== GET STATS ===================== */
  static async getStats(): Promise<NewsletterStats> {
    try {
      const collectionRef = collection(db, COLLECTION);

      const [totalSnap, activeSnap] = await Promise.all([
        getCountFromServer(query(collectionRef)),
        getCountFromServer(query(collectionRef, where('isActive', '==', true))),
      ]);

      const totalSubscribers = totalSnap.data().count;
      const activeSubscribers = activeSnap.data().count;

      return {
        totalSubscribers,
        activeSubscribers,
        inactiveSubscribers: totalSubscribers - activeSubscribers,
      };
    } catch (error) {
      console.error('Error fetching newsletter stats:', error);
      throw error;
    }
  }

  /* ===================== GET PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string
  ): Promise<PaginatedNewsletterResult> {
    try {
      const subscribers: NewsletterSubscriber[] = [];
      const collectionRef = collection(db, COLLECTION);

      const orderConstraint = orderBy('subscribedAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(query(collectionRef));
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const subscriber: NewsletterSubscriber = {
          id: docSnap.id,
          email: data.email,
          isActive: data.isActive ?? false,
          subscribedAt: data.subscribedAt?.toDate(),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          if (subscriber.email.toLowerCase().includes(searchLower)) {
            subscribers.push(subscriber);
          }
        } else {
          subscribers.push(subscriber);
        }
      });

      return {
        subscribers,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated newsletter subscribers:', error);
      throw error;
    }
  }

  /* ===================== DELETE ===================== */
  static async delete(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTION, id));
    } catch (error) {
      console.error('Error deleting newsletter subscriber:', error);
      throw error;
    }
  }

  /* ===================== DELETE MANY ===================== */
  static async deleteMany(ids: string[]): Promise<void> {
    try {
      await Promise.all(ids.map(id => deleteDoc(doc(db, COLLECTION, id))));
    } catch (error) {
      console.error('Error deleting newsletter subscribers:', error);
      throw error;
    }
  }
}